import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { resendAuthInvite } from '@/lib/api/auth-resend-invite'
import { testIds } from '@/lib/test-ids'
import { AuthLayout } from './auth-layout'
import { AuthFormAlert } from './auth-form-alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const schema = z.object({
  email: z.string().email('Enter a valid email'),
})

type Values = z.infer<typeof schema>

export function ExpiredInviteForm() {
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  const onSubmit = async (values: Values) => {
    setError(null)
    try {
      const result = await resendAuthInvite(values.email)
      if (result.rateLimited) {
        setError('Too many requests. Try again in an hour.')
        return
      }
      if (!result.ok) {
        setError('Could not send right now. Try again in a minute.')
        return
      }
      setSent(true)
    } catch {
      setError('Could not send right now. Try again in a minute.')
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        description="If that address has a Maater invite, we sent a new link. It lasts 1 hour."
      >
        <p className="text-center text-sm text-muted-foreground">
          <Link
            to="/login"
            className="text-primary underline-offset-4 hover:underline"
          >
            Back to log in
          </Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="This invite link expired"
      description="Invite links last 1 hour and can only be used once. Enter the email you were invited with and we'll send a new invite."
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          data-testid={testIds.auth.expiredInviteForm}
        >
          {error && <AuthFormAlert message={error} />}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    data-testid={testIds.auth.expiredInviteEmail}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
            data-testid={testIds.auth.expiredInviteSubmit}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Send a new invite'
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Already have a password?{' '}
            <Link
              to="/forgot-password"
              className="text-primary underline-offset-4 hover:underline"
            >
              Forgot password
            </Link>
          </p>
        </form>
      </Form>
    </AuthLayout>
  )
}
